/**
 * The browser glue that makes `SignalDeviceLink` real (ADR-0027 D4.4).
 *
 * The shell asks for two things — an identity to put in the QR, and a publish
 * once a primary has vouched — and this is the only place that knows a worker,
 * a wasm module or the key directory exist. Keeping it out of `shell.ts` is not
 * tidiness: D4.2 says no capability reaches the signal module, and a seam this
 * narrow is one a reviewer can check.
 */
import { SignalClient } from "./signal-client.ts";
import { MessageCache } from "./message-cache.ts";
import type { CachedMessage, CacheStorage } from "./message-cache.ts";
import { EncryptedInbox } from "./encrypted-inbox.ts";
import type { InboxTransport } from "./encrypted-inbox.ts";
import type { ApiClient } from "../shell/api-client.ts";
import type { SignalDeviceLink } from "../shell/shell.ts";

const b64 = (bytes: Uint8Array): string => {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
};

/** How many one-time prekeys to publish. Peers consume one per new session; a
 *  browser that runs out is still reachable (PQXDH works without one) but with
 *  weaker forward secrecy on the first message, so top-up is a follow-up, not
 *  an optional nicety. */
const ONE_TIME_PREKEYS = 20;

export function createSignalDeviceLink(
  client: SignalClient,
  api: ApiClient,
  userId: () => string | null,
  wasmUrl: string,
  /** Where decrypted messages live (ADR-0027 D4.6). Optional so every test and
   *  every build without IndexedDB gets a browser that links and signs in but
   *  reads nothing — which is a real state, not a crash. */
  cacheStorage?: () => Promise<CacheStorage>,
): SignalDeviceLink {
  // Both are per-ACCOUNT and both are opened lazily, because a sign-out
  // followed by a different sign-in never reloads the page: anything captured
  // at construction would still belong to whoever was here before.
  let cached: { userId: string; cache: MessageCache; inbox: EncryptedInbox } | null = null;

  async function forAccount(account: string) {
    if (cached?.userId === account) return cached;
    if (!cacheStorage) return null;
    const cache = await MessageCache.open(await cacheStorage(), account);
    const inbox = new EncryptedInbox({
      signal: client,
      cache,
      transport: currentTransport(),
      deviceRowId: async () => (await client.open(account, wasmUrl)).deviceRowId,
      onMessage: (m) => deliver(m),
      onSyncProgress: (seq, total) => progress(seq, total),
    });
    cached = { userId: account, cache, inbox };
    return cached;
  }

  // The transport and the two callbacks are read at CALL time, never captured
  // into the inbox. The pushes ride the shell's `user:<id>` channel, which is
  // replaced on every reconnect, and an inbox built once with the first
  // caller's callback would silently keep answering the wrong one afterwards.
  // `readable()` alone would have installed a no-op forever.
  let transport: InboxTransport | null = null;
  let deliver: (m: CachedMessage) => void = () => {};
  let progress: (seq: number, total: number) => void = () => {};

  function currentTransport(): InboxTransport {
    return {
      fetch: (deviceRowId) => {
        if (!transport) throw new Error("no realtime channel to read the inbox on");
        return transport.fetch(deviceRowId);
      },
      ack: (deviceRowId, ids) => {
        if (!transport) throw new Error("no realtime channel to ack on");
        return transport.ack(deviceRowId, ids);
      },
    };
  }

  return {
    async identity() {
      // A device is opened against an ACCOUNT, and at QR time there is no
      // account yet. The pending id is the browser's own: the vault keys on it,
      // and `open` after the real sign-in will not find this device — which is
      // correct, because a browser that has not been approved is not a device.
      const id = userId() ?? "pending";
      const device = await client.open(id, wasmUrl);
      return { identityKey: b64(device.identityKey), registrationId: device.registrationId };
    },

    async adoptAndPublish(account, deviceNumber, linkSignature) {
      // The account arrives as an argument and is NOT read from `userId()`.
      // This runs before the shell adopts the session, so `userId()` is still
      // null here — asking it produced a browser that signed in and quietly
      // never became a device (s157, seen against the live stack).
      if (!account) throw new Error("cannot link a device before the account is known");
      await client.adopt(account, deviceNumber, "");
      const bundle = await client.publishBundle(ONE_TIME_PREKEYS);
      const summary = await api.e2eRegisterDevice({
        registration_id: bundle.registrationId,
        device_id: deviceNumber,
        identity_key: b64(bundle.identityKey),
        signed_prekey_id: bundle.signedPrekeyId,
        signed_prekey_public: b64(bundle.signedPrekeyPub),
        signed_prekey_signature: b64(bundle.signedPrekeySig),
        kyber_prekey_id: bundle.kyberPrekeyId,
        kyber_prekey_public: b64(bundle.kyberPrekeyPub),
        kyber_prekey_signature: b64(bundle.kyberPrekeySig),
        // D4.3 admits exactly one signer: the primary. This is 1 by definition,
        // and the server's own CHECK constraint says so too.
        linked_by_device_id: 1,
        link_signature: linkSignature,
      });
      // Only now does the device have a server row id. Recording it after the
      // publish rather than before means a failed publish leaves a device that
      // knows it is not registered, instead of one that thinks it is.
      await client.adopt(account, deviceNumber, summary.id);
      if (bundle.oneTimePrekeys.length > 0) {
        await api.e2eUploadPrekeys(
          summary.id,
          bundle.oneTimePrekeys.map((p) => ({ key_id: p.id, public_key: b64(p.pub) })),
        );
      }
    },

    async attach() {
      const account = userId();
      if (!account) return false;
      // Ask the VAULT, not a flag set earlier in this page's life. Whether this
      // browser is a device is a fact that outlives the tab, and after a reload
      // the only thing that still knows it is the encrypted record on disk.
      //
      // A browser that has never linked gets a device created here and no
      // server row with it, so it answers false — which is the truth, and is
      // also what the next QR will replace.
      const device = await client.open(account, wasmUrl);
      return !!device.deviceRowId;
    },

    async drain(t, onMessage, onSyncProgress) {
      const account = userId();
      if (!account) return 0;
      transport = t;
      deliver = onMessage;
      progress = onSyncProgress ?? (() => {});
      const held = await forAccount(account);
      if (!held) return 0;
      return held.inbox.drain();
    },

    async readable(chatId) {
      const account = userId();
      if (!account || !cacheStorage) return [];
      const held = await forAccount(account);
      return held ? held.cache.forChat(chatId) : [];
    },

    /**
     * ADR-0007 — this browser's safety number against `peerUserId`.
     *
     * ## Why it has to try more than one device
     *
     * A fingerprint is computed from two DEVICE identity keys, and the shim
     * reads the peer's from this device's identity store — so it exists only
     * for a peer device this browser has actually talked to. A browser is a
     * receiving device: the sessions it holds are with whichever of the peer's
     * devices has SENT it an envelope, which is not necessarily their primary
     * and is never all of them. So the peer's device list is walked in
     * ascending device number (their oldest device first, which is the primary
     * for every account that has not re-linked) and the first that answers
     * wins. A device with no session throws, and that is an ordinary miss, not
     * a failure to report.
     *
     * ## What it does NOT do
     *
     * It never fetches a prekey bundle to manufacture a session. That would
     * burn a one-time prekey on the peer every time somebody opened a screen,
     * and it would produce a number for a device this browser has never heard
     * from — which is a fingerprint of nothing anybody exchanged.
     */
    async safetyNumber(peerUserId) {
      const account = userId();
      if (!account || !peerUserId) return null;
      // The device must be open before its identity store can be read; a
      // browser that is not a device has nothing to fingerprint WITH.
      const device = await client.open(account, wasmUrl);
      if (!device.deviceRowId) return null;

      let refs;
      try {
        refs = (await api.peerDevices(peerUserId)).devices ?? [];
      } catch {
        return null;
      }
      const numbers = refs
        .map((d) => d.device_id)
        .filter((n): n is number => typeof n === "number")
        .sort((a, b) => a - b);

      for (const peerDevice of numbers) {
        try {
          const { safetyNumber } = await client.safetyNumber(peerUserId, peerDevice);
          if (safetyNumber) return { digits: safetyNumber, peerDevice };
        } catch {
          // No session with THAT device. Ordinary — try the next one.
        }
      }
      return null;
    },
  };
}
